import { getVerdict, type IntelData } from "../lib/translate";
import { formatPrice } from "../lib/tradingDisplay";
import { buildCommandCenterActionability, type CommandCenterActionabilityState } from "../lib/commandCenterActionability";

export interface CommandCenterActionTradeLike {
  status?: string;
  symbol?: string;
  agent?: string;
  direction?: string;
  session?: string;
  entry_price?: number;
  stop_loss?: number;
  tp1?: number;
  tp2?: number;
}

export interface CommandCenterActionRow {
  symbol: string;
  basis: string;
  decision: string;
  confidence: number;
  actionability: string;
  actionabilityState: CommandCenterActionabilityState;
  actionabilityReason: string;
  dataStatus: string;
  price: string;
  position: string;
  riskReward: string;
  orderFlow: string;
  squeeze: string;
  topFactors: string;
}

interface CommandCenterActionMatrixProps {
  symbols: readonly string[];
  intelMap: Record<string, IntelData | undefined>;
  openTrades: CommandCenterActionTradeLike[];
  session: "asia" | "us" | "all";
}

const S = {
  wrap: {
    background: "rgba(12,12,24,0.88)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "10px",
    padding: "10px",
    marginBottom: "12px",
  },
  title: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  heading: {
    color: "#f8fafc",
    fontSize: "12px",
    fontWeight: 900,
    letterSpacing: "0.05em",
    textTransform: "uppercase" as const,
  },
  subtitle: {
    color: "#94a3b8",
    fontSize: "10px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
    fontSize: "10px",
  },
  th: {
    color: "#94a3b8",
    fontSize: "8px",
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
    textAlign: "left" as const,
    padding: "5px 6px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },
  td: {
    color: "#dbeafe",
    padding: "7px 6px",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    verticalAlign: "middle" as const,
  },
  symbol: {
    color: "#f8fafc",
    fontWeight: 900,
    fontSize: "12px",
  },
  pill: {
    display: "inline-block",
    padding: "2px 6px",
    borderRadius: "999px",
    fontSize: "8px",
    fontWeight: 900,
    letterSpacing: "0.04em",
  },
  buy: { color: "#22c55e", background: "rgba(34,197,94,0.12)" },
  sell: { color: "#ef4444", background: "rgba(239,68,68,0.12)" },
  wait: { color: "#94a3b8", background: "rgba(148,163,184,0.12)" },
  mixed: { color: "#f59e0b", background: "rgba(245,158,11,0.12)" },
  tradable: { color: "#22c55e", background: "rgba(34,197,94,0.12)" },
  blocked: { color: "#ef4444", background: "rgba(239,68,68,0.12)" },
};

function rootSymbol(symbol: string | undefined): string {
  return (symbol || "").replace(/[A-Z]\d+$/, "").toUpperCase();
}

function findOpenTrade(openTrades: CommandCenterActionTradeLike[], symbol: string, session: "asia" | "us" | "all") {
  return openTrades.find((trade) => (
    trade.status === "open"
    && rootSymbol(trade.symbol) === symbol
    && (session === "all" || trade.session === session)
  ));
}

function decisionStyle(decision: string) {
  if (["BUY", "BULLISH"].includes(decision)) return { ...S.pill, ...S.buy };
  if (["SELL", "BEARISH"].includes(decision)) return { ...S.pill, ...S.sell };
  if (decision === "MIXED") return { ...S.pill, ...S.mixed };
  return { ...S.pill, ...S.wait };
}

function actionabilityStyle(state: CommandCenterActionabilityState) {
  if (state === "tradable") return { ...S.pill, ...S.tradable };
  if (state === "blocked") return { ...S.pill, ...S.blocked };
  return { ...S.pill, ...S.mixed };
}

function orderFlowLabel(data: IntelData | undefined): string {
  if (!data) return "No data";
  if (data.order_flow_status && data.order_flow_status !== "ok") return data.order_flow_status.replace(/_/g, " ");
  if (data.institutional_direction) {
    const score = typeof data.institutional_score === "number" ? ` ${data.institutional_score.toFixed(2)}` : "";
    return `${data.institutional_direction.replace(/_/g, " ")}${score}`;
  }
  return "Neutral";
}

function dataHealthLabel(data: IntelData | undefined): string {
  if (!data || data.data_status === "missing") return "No data";
  const issues: string[] = [];
  if (data.data_status === "stale") issues.push("snapshot stale");
  if (data.current_price_disagreement === true) issues.push("price disagree");
  if (data.current_price_status === "stale") issues.push("price stale");
  if (data.current_price_status === "missing") issues.push("price missing");
  if (data.order_flow_status === "stale") issues.push("OF stale");
  if (data.volume_flow_status === "stale") issues.push("vol stale");
  if (issues.length > 0) return issues.slice(0, 2).join(" · ");
  const outlierSources = data.current_price_outlier_sources?.trim();
  if (outlierSources) return `Live · outlier excluded ${outlierSources}`;
  return "Live";
}

function squeezeLabel(data: IntelData | undefined): string {
  if (!data) return "No data";
  const parts = [data.squeeze_1h_status, data.squeeze_4h_status, data.squeeze_daily_status]
    .filter((value): value is string => Boolean(value));
  if (parts.length > 0) return parts.map((part) => part.replace(/_/g, " ")).join(" / ");
  if (data.squeeze_fired) return "Fired";
  if (data.squeeze_on) return data.squeeze_stage || "Building";
  return "Off";
}

function riskRewardForLevels(direction: string | undefined, entry: number | undefined, stopLoss: number | undefined, target: number | undefined): string {
  if (typeof entry !== "number" || typeof stopLoss !== "number" || typeof target !== "number") return "Levels missing";
  const normalizedDirection = direction?.toLowerCase();
  const risk = normalizedDirection === "short" || normalizedDirection === "sell"
    ? stopLoss - entry
    : entry - stopLoss;
  const reward = normalizedDirection === "short" || normalizedDirection === "sell"
    ? entry - target
    : target - entry;
  if (risk <= 0 || reward <= 0) return "Invalid levels";
  return `R:R ${(reward / risk).toFixed(1)}x`;
}

export function buildRiskReward(data: IntelData | undefined, trade: CommandCenterActionTradeLike | undefined): string {
  if (trade) return riskRewardForLevels(trade.direction, trade.entry_price, trade.stop_loss, trade.tp1);
  return riskRewardForLevels(data?.direction, data?.entry, data?.stop_loss, data?.tp1);
}

function compactDirection(value: string | undefined): string | null {
  if (!value) return null;
  const normalized = value.toLowerCase();
  if (["up", "bullish", "long", "buying"].includes(normalized)) return "bull";
  if (["down", "bearish", "short", "selling"].includes(normalized)) return "bear";
  return normalized.replace(/_/g, " ");
}

export function orbLifecycleLabel(data: IntelData | undefined): string | null {
  const state = data?.orb_lifecycle_state;
  if (!state) return null;
  const direction = data.orb_lifecycle_direction?.toUpperCase();
  if (state === "pending_retest") return `ORB ${direction || ""} pending retest`.trim();
  if (state === "missed_entry") return `ORB ${direction || ""} missed entry`.trim();
  if (state === "retest_executable") return `ORB ${direction || ""} retest executable`.trim();
  if (state === "expired") return `ORB ${direction || ""} retest expired`.trim();
  return null;
}

export function buildTopFactors(data: IntelData | undefined, trade: CommandCenterActionTradeLike | undefined): string {
  if (!data) return "No live inputs";

  const factors: string[] = [];
  const lifecycle = orbLifecycleLabel(data);
  if (lifecycle) factors.push(lifecycle);
  const directionalVotes = [
    compactDirection(data.kronos_direction),
    compactDirection(data.kronos_4h_direction),
    compactDirection(data.kronos_hourly_direction),
  ].filter(Boolean);
  const bullVotes = directionalVotes.filter((vote) => vote === "bull").length;
  const bearVotes = directionalVotes.filter((vote) => vote === "bear").length;

  if (bullVotes >= 2) factors.push("Trend bull");
  else if (bearVotes >= 2) factors.push("Trend bear");
  else if (directionalVotes.length > 0) factors.push("Trend mixed");

  if (data.institutional_direction) {
    const direction = data.institutional_direction.replace(/_/g, " ");
    const score = typeof data.institutional_score === "number" ? ` ${data.institutional_score.toFixed(2)}` : "";
    factors.push(`Flow ${direction}${score}`);
  } else if (data.order_flow_status && data.order_flow_status !== "ok") {
    factors.push(`Flow ${data.order_flow_status.replace(/_/g, " ")}`);
  }

  if (data.squeeze_fired) factors.push("Squeeze fired");
  else if (data.squeeze_on || data.squeeze_1h_status === "building" || data.squeeze_4h_status === "building") factors.push("Squeeze building");

  if (data.current_price_disagreement === true) factors.push("Price disagree");
  else if (data.current_price_outlier_sources?.trim()) factors.push("Bad price source excluded");

  if (trade) factors.push(`Risk: ${trade.agent?.toUpperCase() || "TRADE"} open`);
  if (data.data_status !== "live") factors.push("Data not live");

  return factors.slice(0, 4).join(" · ") || "Neutral inputs";
}

function hasSignalLevels(data: IntelData | undefined): boolean {
  return typeof data?.entry === "number" && typeof data?.stop_loss === "number" && typeof data?.tp1 === "number";
}

export function buildDecisionBasis(data: IntelData | undefined, trade: CommandCenterActionTradeLike | undefined): string {
  if (trade) return `${trade.agent?.toUpperCase() || "TRADE"} open`;
  if (!data || data.data_status === "missing") return "No data";
  const lifecycle = orbLifecycleLabel(data);
  if (lifecycle) return lifecycle;
  const agent = data.agent?.toUpperCase();
  if (agent && hasSignalLevels(data)) return `${agent} levels`;
  if (hasSignalLevels(data)) return "Trade levels";
  return "Live intel only";
}

export function buildCommandCenterActionRows(
  symbols: readonly string[],
  intelMap: Record<string, IntelData | undefined>,
  openTrades: CommandCenterActionTradeLike[],
  session: "asia" | "us" | "all",
): CommandCenterActionRow[] {
  return symbols.map((symbol) => {
    const data = intelMap[symbol] || ({ symbol } as IntelData);
    const verdict = getVerdict(data);
    const actionability = buildCommandCenterActionability(data, verdict.action);
    const trade = findOpenTrade(openTrades, symbol, session);
    return {
      symbol,
      basis: buildDecisionBasis(data, trade),
      decision: verdict.action,
      confidence: verdict.confidence,
      actionability: actionability.label,
      actionabilityState: actionability.state,
      actionabilityReason: actionability.reason,
      dataStatus: dataHealthLabel(data),
      price: typeof data.current_price === "number" ? formatPrice(data.current_price, symbol) : "—",
      position: trade ? `${trade.agent?.toUpperCase() || "TRADE"} ${trade.direction?.toUpperCase() || "OPEN"}` : "None",
      riskReward: buildRiskReward(data, trade),
      orderFlow: orderFlowLabel(data),
      squeeze: squeezeLabel(data),
      topFactors: buildTopFactors(data, trade),
    };
  });
}

export function CommandCenterActionMatrix({ symbols, intelMap, openTrades, session }: CommandCenterActionMatrixProps) {
  const rows = buildCommandCenterActionRows(symbols, intelMap, openTrades, session);
  return (
    <section style={S.wrap} aria-label="Command Center action matrix">
      <div style={S.title}>
        <span style={S.heading}>Line-by-line action matrix</span>
        <span style={S.subtitle}>Trading cockpit only · no audit metrics</span>
      </div>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Symbol</th>
            <th style={S.th}>Basis</th>
            <th style={S.th}>Decision</th>
            <th style={S.th}>State</th>
            <th style={S.th}>Data</th>
            <th style={S.th}>Price</th>
            <th style={S.th}>Position</th>
            <th style={S.th}>R/R</th>
            <th style={S.th}>Top factors</th>
            <th style={S.th}>Order flow</th>
            <th style={S.th}>Squeeze</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.symbol}>
              <td style={{ ...S.td, ...S.symbol }}>{row.symbol}</td>
              <td style={S.td}>{row.basis}</td>
              <td style={S.td}><span style={decisionStyle(row.decision)}>{row.decision} {row.confidence > 0 ? `${row.confidence}%` : ""}</span></td>
              <td style={S.td}><span title={row.actionabilityReason} style={actionabilityStyle(row.actionabilityState)}>{row.actionability}</span></td>
              <td style={S.td}>{row.dataStatus}</td>
              <td style={S.td}>{row.price}</td>
              <td style={S.td}>{row.position}</td>
              <td style={S.td}>{row.riskReward}</td>
              <td style={S.td}>{row.topFactors}</td>
              <td style={S.td}>{row.orderFlow}</td>
              <td style={S.td}>{row.squeeze}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
